import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TransactionStatus } from 'globalpayments-3ds';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BACK, CANCEL, CREATE_ACCOUNT, NEXT, UPDATE } from '../cp-common/signup-footer-controls/translate';
import { setControlMetadata } from '../helpers/utils';
import { LangPipe } from '../pipes/lang.pipe';
import { AppConfigService } from '../services/app-config.service';
import { CommunicationService } from '../services/communication.service';
import { DiscoveryStoreService } from '../services/discovery.service';
import { FrocusTrapService } from '../services/frocus-trap.service';
import { FrontendContentConfigService } from '../services/frontend-content-config.service';
import { UserFromSession } from '../services/interfaces/UserFromSession';
import { LanguageService } from '../services/language.service';
import { CPMixPanel } from '../services/mixpanel.service';
import { Payment } from '../services/models/backend-save-classes';
import { SessionService } from '../services/session.service';
import { GENERAL_SERVER_ERROR } from '../services/translation-files/translate';
import { UiFlowStateService } from '../services/ui-flow-state.service';
import { UserPaymentService } from './user-payment.service';
import { AdyenCheckout, Card, Dropin } from '@adyen/adyen-web';
import {
  AdyenAuthorizeCardRequest,
  AdyenAuthorizeCardResponse,
  AdyenEncryptedCard,
  AdyenPaymentMethodsResponse,
} from '../services/interfaces/Adyen';

// Survives the full-page redirect leg of a 3DS challenge. Same key name
// web-driver uses, though the two apps have separate sessionStorage origins.
const ADYEN_PENDING_PAYMENT_UUID_KEY = 'adyenPendingPaymentUuid';
const ADYEN_REDIRECT_RESULT_PARAM = 'redirectResult';

enum CreditCardType {
  MASTERCARD,
  VISA,
  AMEX,
  DISCOVER,
}

@Component({
  selector: 'cp-user-payment',
  templateUrl: './user-payment.component.html',
  styleUrls: ['./user-payment.component.scss']
})
export class UserPaymentComponent implements OnInit, AfterViewInit, OnDestroy {
  isModalOpen = false;
  pressedSubmit = false;
  checkedPromo = false;
  acceptedPromo = false;
  invalidCreditCardCode = 0;
  isPromoEmpty = true;
  responsePromo: any = {};
  currencyCode = '';
  isLoading = false;
  masterObj: any = {};
  optional = false;
  orgName: string;
  cost = '10';
  costCurrencyCode = 'USD';
  formattedCost = '';
  lastComponent: Boolean;
  locale = 'en_US';
  showProgramPopup = false;
  programLink: SafeResourceUrl;
  langPipe: LangPipe;
  countryCode: '';
  ccTypeUrl: string;
  ccTypeAltText: string;
  region: string;

  signupType: string;

  ccLast4: string;
  ccLast4Cache: string;
  backButtonPreventDefault = false;
  backButtonText = null;
  nextButtonText = null;
  paypalExpressCheckout: string;
  isCC3DSecureEnabled = false;

  showBackButton = true;
  showCancelButton = false;
  showPromoCode = false;
  showSubHeader = true;
  isPayPerCharge = null;
  userFromSession: UserFromSession;

  // --- Adyen card-on-file (PREQ-2455 / DRIPOR-697) ---------------------
  // When useAdyen is true the legacy Global Payments inputs are replaced by
  // the Adyen Drop-in, mounted into #adyenRoot. Drop-in renders Adyen's own
  // card UI, so the processor in use is visible to both drivers and QA —
  // previously we injected secured fields into GP-styled holders, which made
  // an Adyen-routed form indistinguishable from Global Payments and led to it
  // being tested with Global Payments cards (which Adyen correctly declines).
  //
  // Drop-in's own pay button is suppressed (showPayButton: false) so signup
  // keeps its Back/Next footer and postal-code input; Next drives
  // dropin.submit(), and the encrypted card arrives via the checkout onSubmit
  // callback. If discovery has no Adyen key, the backend routes this driver
  // elsewhere, or init/mount fails, we fall back to the GP form.
  useAdyen = false;
  // True only while initAdyenCheckout()'s async determination (the
  // payment-methods routing call, then the Adyen SDK's own init) is in
  // flight for a driver who *might* be Adyen-eligible. useAdyen defaults to
  // false and is only flipped once that determination resolves, so without
  // this the fully-rendered, interactive Global Payments form always paints
  // first and then flickers to the Adyen Drop-in once it's ready. The
  // template hides both forms while this is true and shows a spinner
  // instead. Drivers who were never going to be Adyen-eligible (discovery
  // has no client key, EU, Daimler) never see this — maybeInitAdyen()
  // returns before ever setting it, so GP renders immediately as before.
  adyenPending = false;
  private adyenValid = false;
  private adyenTouched = false;
  private adyenLast4 = '';
  private adyenCheckout: any = null;
  // Adyen's own analytics id for this checkout attempt. It ties the client-side
  // session to the authorize call in Adyen's dashboard and risk engine, so all
  // three other surfaces send it: web-driver (EditPaymentInfo), Android
  // (CardAdyenAdditionalData.checkoutAttemptId) and iOS. Produced by the SDK's
  // analytics module, which is why `analytics: { enabled: true }` matters below.
  private adyenCheckoutAttemptId: string | undefined;
  private adyenCardComponent: any = null;
  private adyenRootEl: HTMLElement | null = null;
  private adyenMountScheduled = false;

  errorTranslationKeys = {
    ccNumber: 'errorLabelCcNumber',
    ccExpirationDate: 'errorLabelCcExpirationDate',
    ccCVV: 'errorLabelCcCVV',
    ccPostalCode: 'errorLabelCcPostalCode',
  };

  @ViewChild('promoCode') promoCode: ElementRef<HTMLInputElement>;
  @ViewChild('creditCard') creditCard: ElementRef<HTMLInputElement>;

  // Mount root for the Adyen Drop-in. Lives inside *ngIf="!ccLast4", so the
  // setter fires whenever the card form is (re)shown — that's when we mount.
  @ViewChild('adyenRoot') set adyenRootRef(ref: ElementRef<HTMLElement> | undefined) {
    this.adyenRootEl = ref ? ref.nativeElement : null;
    this.maybeMountAdyen();
  }

  payForm: FormGroup = new FormGroup({
    creditcard: new FormControl('', {
      validators: [Validators.required],
      updateOn: 'change'
    }),
    expirationdate: new FormControl('', {
      validators: [Validators.required, this.isExpirationCorrect()],
      updateOn: 'change'
    }),
    cvv: new FormControl('', {
      validators: [Validators.required],
      updateOn: 'change'
    }),
    postalcode: new FormControl('', {
      validators: [Validators.required],
      updateOn: 'change'
    }),
  });

  promoForm: FormGroup = new FormGroup({
    promo: new FormControl('', {
      validators: [Validators.required],
      updateOn: 'change'
    }),
  });

  constructor(
    private sanitationService: DomSanitizer,
    private zone: NgZone,
    private userService: UserPaymentService,
    private commSvc: CommunicationService,
    private mixPanel: CPMixPanel,
    private frontendConfigSvc: FrontendContentConfigService,
    private langSvc: LanguageService,
    private appConfigService: AppConfigService,
    private sessionService: SessionService,
    private focusTrapService: FrocusTrapService,
    private stateSvc: UiFlowStateService,
    private discoveryService: DiscoveryStoreService
    ) {
    this.isLoading = window.location.href.includes('/signup/paypal-success');
  }

  ngOnInit() {
    this.langPipe = new LangPipe(this.langSvc);
    this.setFormControlsMetadata();
    this.frontendConfigSvc.setBodyTitle('paymentTitle');
    this.showSubHeader = this.frontendConfigSvc.getLayoutConfig('payment').showSubHeader;
    this.locale = this.langSvc.getLocale();

    this.currencyCode = this.discoveryService.getDefaultCurrencyCode();
    this.costCurrencyCode = this.discoveryService.getDefaultCurrencyCode();
    this.masterObj = this.commSvc.getMasterObject();
    this.region = this.discoveryService.getDiscoveryData().endPoints.accounts_endpoint.value.includes('eu') ? 'EU' : '';

    this.appConfigService.getConfig().subscribe(data => {
      this.paypalExpressCheckout = data.paypalExpressCheckout;
      this.isCC3DSecureEnabled = data.isCC3DSecureEnabled;
    });

    this.sessionService.getUser().subscribe(user => {
      this.userFromSession = user;
      if (Array.isArray(user.billingModels) && user.billingModels.length > 1) {
        this.isPayPerCharge = false;
      } else {
        this.isPayPerCharge = Array.isArray(user.billingModels) && user.billingModels.includes('PAY_PER_CHARGE');
      }
      if (!this.masterObj['profile_zipCode'] && user?.address?.zipCode) {
        this.payForm.get('postalcode').patchValue(user.address.zipCode);
      }
    });

    this.commSvc.getComponentDefinition('payment').subscribe(componentDef => {
      if (componentDef) {
        componentDef.fields.forEach( field => {
          if (field.id === 'promoCode') {
            this.showPromoCode = true;
          }
        });
      }
    });

    this.payForm.get('postalcode').patchValue(this.masterObj['profile_zipCode']);

    if ('paymentUuid' in this.masterObj) {
      this.ccLast4 = this.masterObj.ccLast4;
    }

    if ('promo' in this.masterObj) {
      this.responsePromo = this.masterObj['promo'];
      this.acceptedPromo = true;
      if (!this.responsePromo.paymentRequired) {
        this.clearCardValidation();
      }
    }

    if ('processPayPalToken' in this.masterObj && this.masterObj.processPayPalToken) {
      this.isLoading = true;
    }
    // setup optional
    this.optional = this.masterObj.paymentOptional;
    this.signupType = this.masterObj.signupType;
    this.lastComponent = this.masterObj.createComponent === 'payment';

    if (this.lastComponent && !this.masterObj.signupEmail && this.frontendConfigSvc.cobrand === 'daimler') {
      this.nextButtonText = this.langPipe.transform(CREATE_ACCOUNT);
    } else {
      this.nextButtonText = this.langPipe.transform(NEXT);
    }

    if (this.commSvc.getPartnerToken() && this.signupType === 'LEASECO') {
      this.userService.getLeascoFields(this.commSvc.getPartnerToken()).subscribe(data => {
        this.orgName = data.orgName;
      });
    }
    this.commSvc.getComponentDefinition('payment').subscribe(componentDefinition => {
      if (componentDefinition) {
        this.showBackButton = componentDefinition.showBackButton;
      }
    });

    if (this.masterObj.profile_countryCode) {
      this.countryCode = this.masterObj.profile_countryCode;
    } else if (this.masterObj.selectedCountryObj && this.masterObj.selectedCountryObj.code) {
      this.countryCode = this.masterObj.selectedCountryObj.code;
    }

    this.userService.getReplenishmentAmount(this.countryCode).subscribe((resp) => {
      if (resp.initialDeposit) {
        this.cost = resp.initialDeposit;
      }

      if (resp.code) {
        this.costCurrencyCode = resp.code;
        this.currencyCode = resp.code;
      }

      this.formattedCost = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.costCurrencyCode,
      }).format(Number(this.cost)).replace(',', '.');
    });

    this.ccTypeUrl = '/assets/images/ic_credit_generic.png';
    this.ccTypeAltText = '';

    this.maybeInitAdyen();
   }

  private setFormControlsMetadata() {
    setControlMetadata(
      'creditcard',
      this.payForm,
      {translatedLabel: this.langPipe.transform(this.errorTranslationKeys.ccNumber)}
    );
    setControlMetadata(
      'expirationdate',
      this.payForm,
      {translatedLabel: this.langPipe.transform(this.errorTranslationKeys.ccExpirationDate)}
    );
    setControlMetadata(
      'cvv',
      this.payForm,
      {translatedLabel: this.langPipe.transform(this.errorTranslationKeys.ccCVV)}
    );
    setControlMetadata(
      'postalcode',
      this.payForm,
      {translatedLabel: this.langPipe.transform(this.errorTranslationKeys.ccPostalCode)}
    );
  }

  ngAfterViewInit() {
    if (this.creditCard) {
      this.creditCard.nativeElement.focus();
    }
  }

  modalToggle(e?) {
    // EE_TODO: fix animation
    // EE_TODO: add class to body to not have overflow
    if (e) {
      this.isModalOpen = !e.target.dataset.close;
    } else {
      this.isModalOpen = true;
      setTimeout(() => {
        this.promoCode.nativeElement.focus();
      }, 100);
    }
  }

openModal(e) {
  this.focusTrapService.setLastFocusedElement(e.target as HTMLElement);
  this.modalToggle();
}

  closeProgramPopup(webviewData) {
    // VC: CPSRV-24763 Fix for program iframe
    this.zone.run(() => {
      this.programLink = '';
      this.showProgramPopup = false;
      this.acceptedPromo = true;
      this.checkedPromo = true;
      this.isModalOpen = false;
      this.clearCardValidation();
      this.commSvc.pushToMaster(
        {'additionalFields': webviewData},
        {'additionalFields': webviewData}
        );

      if (this.isLoading) {
        this.isLoading = false;
      }
    });
  }

  checkPromo() {
    if (this.promoForm.get('promo').value.length > 0) {
      this.isLoading = true;
      this.userService.checkPromo(this.promoForm.get('promo').value)
      .subscribe(data => {
        this.responsePromo = data;
        if (data.program && data.program.webViewLink) {
          // VC: CPSRV-24763 Fix for program iframe
          window['CP'] = {my_account: true};
          window['my_close_function'] = this.closeProgramPopup.bind(this);
          this.programLink = this.sanitationService.bypassSecurityTrustResourceUrl(data.program.webViewLink);
          this.showProgramPopup = true;
        } else {
          this.acceptedPromo = true;
          this.checkedPromo = true;
          this.isModalOpen = false;
        }
        if (!data.paymentRequired) {
          this.clearCardValidation();
        }
        this.isLoading = false;
      }, err => {
        // if promo code is not valid
        this.responsePromo = err.error;
        this.checkedPromo = true;
        this.isLoading = false;
      });
    } else {
      this.checkedPromo = true;
      this.isPromoEmpty = false;
    }
  }

  submitPaypal() {
    if(this.region !== 'EU') {
      // Spinner
      this.isLoading = true;
      const ppObj: any = {
        'amount': 0,
        'cancelUrl': `${window.location.protocol}//${window.location.host}/signup/paypal-failure${window.location.search}`,
        'currency': this.currencyCode,
        'returnUrl': `${window.location.protocol}//${window.location.host}/signup/paypal-success${window.location.search}`
      };

      this.userService.paypalSetExpressCheckout(ppObj)
      .pipe(
        catchError(() => {
          this.stateSvc.updateErrorMessage(GENERAL_SERVER_ERROR);
          return of(null);
        })
      )
      .subscribe(data => {
        if (data === null) {
          this.isLoading = false;
          return;
        }
        this.mixPanel.paymentPayPalContinue();
        this.redirectToPaypal(data.token);
      });
    } else {
      // Do Nothing for now.
    }
  }

  getCCType(s) {
    if (s.match(new RegExp('^(?:5[1-6]|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)'))) {
      return CreditCardType.MASTERCARD;
    }

    if (s.match(new RegExp('^4'))) {
      return CreditCardType.VISA;
    }

    if (s.match(new RegExp('^3[47]'))) {
      return CreditCardType.AMEX;
    }

    if (s.match(new RegExp('^6(?:011|5[0-9]{2})'))) {
      return CreditCardType.DISCOVER;
    }

    return undefined;
  }

  changeCreditCardNumber(e) {
    const ccType = this.getCCType(e.target.value);

    switch (ccType) {
      case CreditCardType.AMEX:
        this.ccTypeUrl = '/assets/images/ic_credit_amex.png';
        this.ccTypeAltText = 'American Express';
        break;
      case CreditCardType.DISCOVER:
        this.ccTypeUrl = '/assets/images/ic_credit_discover_cp.png';
        this.ccTypeAltText = 'Discover';
        break;
      case CreditCardType.MASTERCARD:
        this.ccTypeUrl = '/assets/images/mc_symbol.svg';
        this.ccTypeAltText = 'Mastercard';
        break;
      case CreditCardType.VISA:
        this.ccTypeUrl = '/assets/images/ic_credit_visa.png';
        this.ccTypeAltText = 'VISA';
        break;
      default:
        this.ccTypeUrl = '/assets/images/ic_credit_generic.png';
        this.ccTypeAltText = '';
        break;
    }
  }

  redirectToPaypal(token) {
    window.location.href = this.paypalExpressCheckout + token;
  }

  skipSubmit() {
    this.mixPanel.paymentSkip();
    const backendSaveObj = new Payment({paymentUuid: ''});
    this.commSvc.submitFormFields({paymentUuid: ''}, backendSaveObj);
  }

  handleEnterKey(event: KeyboardEvent) {
    this.submitProfile();
    event.stopPropagation();
    event.preventDefault();
  }

  submitProfile() {
    this.pressedSubmit = true;
    if (this.useAdyen) {
      this.submitAdyenProfile();
      return;
    }
    if (this.payForm.status === 'PENDING') {
      const validaionSubscription = this.payForm.statusChanges.subscribe(status => {
        if (status === 'VALID') {
          this.executePaymentFlow();
          validaionSubscription.unsubscribe();
        } else if (status === 'INVALID') {
          validaionSubscription.unsubscribe();
        }
      });
    } else if (this.payForm.valid || this.ccLast4) {
      this.executePaymentFlow();
    }
  }

  executePaymentFlow() {
    if (this.paymentPromoPreviouslySet()) {
      const backenddata: any = {};
      if (this.masterObj.paymentUuid) {
        const cardNumber = this.payForm.get('creditcard').value;
        const currentLastCC4 = cardNumber.slice(cardNumber.length - 4);
        if (this.masterObj.ccLast4 && this.masterObj.ccLast4 === currentLastCC4) {
          backenddata.paymentUuid = this.masterObj.paymentUuid;
        } else {
          // credit card was changed
          delete this.masterObj.paymentUuid;
          delete this.masterObj.promoCode; // this will update on second iteration.
          this.executePaymentFlow();
          return;
        }
      }

      if (this.masterObj.promoCode) {
        backenddata.promoCode = this.masterObj.promoCode;
      }
      this.commSvc.submitFormFields(backenddata, backenddata);
    } else if (this.useAdyen && this.adyenValid) {
      // Drop-in owns encryption, so we cannot build the request here. Ask it to
      // submit; the flow resumes in onAdyenDropinSubmit with the encrypted card.
      if (!this.adyenCardComponent || typeof this.adyenCardComponent.submit !== 'function') {
        return;
      }
      this.isLoading = true;
      this.adyenCardComponent.submit();
    } else if (this.shouldValidateCC() && this.payForm.valid) {
      this.isLoading = true;
      this.authorizeCC().then(
        (data) => this.onAuthorizeSuccess(data),
        (err) => this.onAuthorizeError(err)
      );
    } else if (this.acceptedPromo) {
      this.isLoading = false;
      this.commSvc.submitFormFields({'promo': this.responsePromo}, {'promoCode': this.responsePromo.code});
    } else {
      this.isLoading = false;
    }
  }

  paymentPromoPreviouslySet() {
    return (typeof this.masterObj.paymentUuid !== 'undefined' || typeof this.masterObj.promoCode !== 'undefined');
  }

  shouldValidateCC() {
    return this.payForm.get('creditcard').value !== ''
        || this.payForm.get('cvv').value !== ''
        || this.payForm.get('expirationdate').value !== '';
  }

  authorizeCC() {
    const masterObj = this.commSvc.getMasterObject();
    if (!masterObj['givenName'] && this.userFromSession?.user?.givenName) {
      masterObj['givenName'] = this.userFromSession.user.givenName;
    }
    if (!masterObj['familyName'] && this.userFromSession?.user?.familyName) {
      masterObj['familyName'] = this.userFromSession.user.familyName;
    }
    const monthYearArr = this.payForm.get('expirationdate').value.split('/');
    const ccObj: any = {
      'address': {
        'zipCode': this.payForm.get('postalcode').value,
        'countryCode': this.countryCode
      },
      'cardNumber': this.payForm.get('creditcard').value,
      'currency': this.currencyCode,
      'cvv': this.payForm.get('cvv').value,
      'email': masterObj['email'],
      'expirationMonth': Number(monthYearArr[0]),
      'expirationYear': Number(monthYearArr[1]),
      'fullName': `${masterObj['givenName']} ${masterObj['familyName']}`,
      'paymentType': 'AUTO_TOP'
    };

    if (!this.isCC3DSecureEnabled) {
      return this.userService.authorizeCreditCard(ccObj, this.commSvc.getSessionToken());
    }

    return this.userService.checkCardVersion(this.masterObj, ccObj, this.commSvc.getSessionToken()).then(
      result => {
        if (result.status === TransactionStatus.ChallengeRequired
            && result.challenge?.response?.data
            && 'transStatus' in result.challenge.response.data) {
          this.mixPanel.card3dsChallenge(
            {
              'User Type': 'Driver',
              'Status': result.challenge.response.data.transStatus,
              'Server Transaction ID': result.serverTransactionId,
              'ACS Transaction ID': result.acsTransactionId
            }
          );
        }
        // test cards with statuses can be found at
        // https://developer.globalpay.com/resources/test-card-numbers#3dsecure-2
        if (result.status === TransactionStatus.AuthenticationSuccessful
            || result.status === TransactionStatus.AuthenticationAttemptedButNotSuccessful) {
          // not all cards support 3D secure check
          return this.userService.authorizeCreditCard(
            ccObj,
            this.commSvc.getSessionToken(),
            result.serverTransactionId
          );
        } else if (result.status === TransactionStatus.ChallengeRequired
            && 'transStatus' in result.challenge.response.data
            && result.challenge.response.data.transStatus === 'Y') {
          // response.challenge.response.data contains the result of challenge flow
          // see IChallengeNotificationData in globalpayments-3ds
          return this.userService.authorizeCreditCard(
            ccObj,
            this.commSvc.getSessionToken(),
            result.challenge?.response?.data?.threeDSServerTransID
          );
        } else {
          let errorId = 500;
          if (result.status !== TransactionStatus.ChallengeRequired) {
            errorId = 401;
          } else if (result.challenge?.response?.data
              && 'transStatus' in result.challenge.response.data
              && result.challenge.response.data.transStatus === 'N') {
            errorId = 504;
          }

          throw {
            error: {
              errorId: errorId
            }
          };
        }
      }
    );
  }

  // Utility function for Validation
  updateError(type) {
    return (this.payForm.get(type).invalid && (this.payForm.get(type).touched && !this.payForm.get(type).pristine))
    || (this.payForm.get(type).invalid && this.pressedSubmit);
  }

  updateErrorPromo() {
    return this.promoForm.get('promo').invalid && this.checkedPromo;
  }

  isExpirationCorrect(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value.length > 0 && control.value.indexOf('/') !== -1) {
        if (control.value[2] !== '/') {
          return { invalid: true };
        } else {
          // if exp date is past or more than 20 years than today then return false;
          const monthYear = control.value.split('/');
          const expMonth = parseInt(monthYear[0], 10);
          const expYear = parseInt(monthYear[1], 10);

          const today = new Date();
          const thisYear = parseInt(today.getFullYear().toString().substring(2), 10);

          const result = (expMonth >= 1 && expMonth <= 12
            && (expYear >= thisYear && expYear < thisYear + 20)
            && (expYear === thisYear ? expMonth >= (today.getMonth() + 1) : true));

          if (!result) {
            return {invalid: true};
          }
        }
      }
      return null;
    };
  }

  isNumber(evt) {
    evt = (evt) ? evt : window.event;
    const charCode = (evt.which) ? evt.which : evt.keyCode;

    // Delete = 46, Backspace = 8, Tab = 9, Space = 32, Home = 36, End = 35
    return !(charCode !== 8 && evt.which !== 0 && (charCode < 48 || charCode > 57));
  }

  expirationSlash(evt) {
    if (evt.target.value.length === 2 && evt.keyCode !== 8) {
      evt.target.value += '/';
    }
  }

  clearCardValidation() {
    this.payForm.get('creditcard').clearValidators();
    this.payForm.get('creditcard').updateValueAndValidity();
    this.payForm.get('expirationdate').clearValidators();
    this.payForm.get('expirationdate').updateValueAndValidity();
    this.payForm.get('cvv').clearValidators();
    this.payForm.get('cvv').updateValueAndValidity();
    this.payForm.get('postalcode').clearValidators();
    this.payForm.get('postalcode').updateValueAndValidity();
  }

  showPaymentForm() {
    this.ccLast4Cache = this.ccLast4;
    this.ccLast4 = null;
    this.backButtonPreventDefault = true;
    this.showCancelButton = true;
    this.backButtonText = this.langPipe.transform(CANCEL);
    this.nextButtonText = this.langPipe.transform(UPDATE);
    this.masterObj.paymentIdCache = this.masterObj.paymentUuid;
    delete this.masterObj.paymentUuid;
    if (this.useAdyen) {
      // Re-show the card form: drop any stale instance/state so the
      // @ViewChild('adyenRoot') setter re-mounts fresh secured fields.
      this.unmountAdyen();
      this.adyenValid = false;
      this.adyenTouched = false;
    }
  }

  cancelClicked() {
    this.ccLast4 = this.ccLast4Cache;
    this.masterObj.paymentUuid = this.masterObj.paymentIdCache;
    this.backButtonText = this.langPipe.transform(BACK);
    this.nextButtonText = this.langPipe.transform(NEXT);
    this.showCancelButton = false;
    if (this.backButtonPreventDefault) {
      this.backButtonPreventDefault = false;
      return;
    }
  }


  // =====================================================================
  // Adyen card-on-file (PREQ-2455)
  // =====================================================================

  private maybeInitAdyen(): void {
    // Mercedes / Daimler users stay on the legacy Global Payments form (Adyen disabled for this cobrand).
    if (this.frontendConfigSvc.isDaimler()) {
      return;
    }
    // Temporary EU guard: the Adyen card/authorize endpoint is not yet deployed
    // on the EU payment host (returns 404), so EU stays on Global Payments until
    // backend deploys it. Remove this guard once the EU endpoint is live.
    if (this.region === 'EU') {
      return;
    }
    if (!this.discoveryService.isAdyenCCEnabled()) {
      return;
    }
    // From here on this driver might end up on Adyen — hold both forms back
    // (see adyenPending's doc comment) until initAdyenCheckout() resolves.
    this.adyenPending = true;
    this.initAdyenCheckout();
  }

  // Backend routing signal. Fall back to Global Payments only when the backend
  // explicitly routes this driver there; otherwise show the Adyen Drop-in.
  //
  // This is exactly the driver portal's rule (EditPaymentInfo isLegacyFlow),
  // so both web surfaces pick the same form for the same response. Signup
  // previously inverted it — Adyen only on an explicit ADYEN /
  // eligibleToAdyen: true — so a response with no processor assigned yet
  // ({ paymentProcessor: null, eligibleToAdyen: false }) sent the driver to
  // Global Payments here while the portal, Android and iOS all showed Adyen.
  // The portal's own comment names that shape as the mid-signup case.
  //
  // The mobile apps agree on that shape but not on every edge case: Android
  // treats a missing eligibleToAdyen as eligible (it checks == false), and iOS
  // ignores the body of a successful response entirely (only a 403 means
  // Global Payments). Matching the portal was the goal here.
  //
  // DRIPOR-698 still holds: a driver the backend explicitly assigns to
  // GLOBALPAYMENTS stays off Adyen, so their card is never sent to
  // /adyen/card/authorize (which answers 422 for them). A missing or empty
  // response also stays on Global Payments rather than guessing.
  private isDriverAdyenEligible(methods: AdyenPaymentMethodsResponse | null | undefined): boolean {
    if (!methods) {
      return false;
    }
    const explicitlyGlobalPayments =
      methods.paymentProcessor === 'GLOBALPAYMENTS' && methods.eligibleToAdyen !== true;
    return !explicitlyGlobalPayments;
  }

  private async initAdyenCheckout(): Promise<void> {
    try {
      // Computed inside the try: this is called fire-and-forget from
      // maybeInitAdyen() with no .catch(), so anything thrown out here
      // (synchronously, before the first await) would be an unhandled
      // promise rejection that leaves adyenPending stuck true forever.
      const countryCode = (this.countryCode as string)
        || this.discoveryService.getDefaultRegionCountry()?.code || 'US';
      this.mixPanel.adyenInitialization({
        'User Type': 'Driver',
        Currency: this.currencyCode,
        Country: countryCode,
      });

      // payment-methods doubles as a readiness probe: a 4xx (e.g. Adyen not
      // enabled for this account) throws → we keep the legacy GP form.
      const methods = await this.userService.getAdyenPaymentMethods(
        this.currencyCode,
        countryCode,
        this.commSvc.getSessionToken()
      );
      this.mixPanel.adyenPaymentMethodsFetched({
        'User Type': 'Driver',
        'Methods Count': methods?.paymentMethods?.length ?? 0,
        'Payment Processor': methods?.paymentProcessor,
        'Eligible To Adyen': methods?.eligibleToAdyen,
      });

      if (!this.isDriverAdyenEligible(methods)) {
        this.zone.run(() => {
          this.useAdyen = false;
          this.adyenPending = false;
        });
        return;
      }

      const checkout = await AdyenCheckout({
        clientKey: this.discoveryService.getAdyenClientKey(),
        environment: this.discoveryService.getAdyenEnvironment(),
        countryCode,
        locale: (this.locale || 'en_US').replace('_', '-'),
        amount: { value: 0, currency: this.currencyCode },
        paymentMethodsResponse: methods,
        analytics: { enabled: true },
        onChange: (state: any) => this.zone.run(() => {
          this.adyenTouched = true;
          this.adyenValid = !!state?.isValid;
        }),
        // Drop-in owns encryption, so the card blobs only exist here. Signup's
        // Next button calls dropin.submit(), which lands in this callback.
        onSubmit: (state: any, component: any) => this.onAdyenDropinSubmit(state, component),
        // Fires after the SDK has run a 3DS fingerprint or challenge, either
        // in-page or on return from a redirect.
        onAdditionalDetails: (state: any, component: any) =>
          this.onAdyenAdditionalDetails(state, component),
        onError: (err: unknown) => this.onAdyenError(err),
      } as any);

      // Read once, here, exactly as web-driver does (web-common's AdyenDropIn
      // reads it off the resolved checkout before mounting Drop-in). There is
      // no callback for it: if Adyen's analytics call has not come back yet the
      // value is undefined and the key is simply omitted from the request,
      // which is what the other surfaces do too.
      this.adyenCheckoutAttemptId = (checkout as any)?.modules?.analytics?.checkoutAttemptId;

      this.zone.run(() => {
        this.adyenCheckout = checkout;
        // The GP card controls are not rendered in Adyen mode; drop their
        // required validators so payForm validity depends only on postal code.
        this.clearGpCardValidators();
        this.useAdyen = true;
        this.adyenPending = false;
        this.maybeMountAdyen();
        this.resumeAdyenRedirectIfNeeded(checkout);
      });
    } catch (err) {
      // Any failure → silent fallback to the legacy Global Payments form.
      this.onAdyenError(err);
      this.zone.run(() => {
        this.useAdyen = false;
        this.adyenPending = false;
      });
    }
  }

  // Redirect-type 3DS actions navigate the whole page to the issuer and back to
  // our returnUrl with ?redirectResult=... appended. Signup survives that round
  // trip because its flow state (masterFormObj / componentId) is rehydrated
  // from sessionStorage on load — the same mechanism PayPal already relies on.
  // Hand the result to the SDK, which re-enters us via onAdditionalDetails.
  private resumeAdyenRedirectIfNeeded(checkout: any): void {
    let redirectResult: string | null = null;
    let url: URL;
    try {
      url = new URL(window.location.href);
      redirectResult = url.searchParams.get(ADYEN_REDIRECT_RESULT_PARAM);
    } catch {
      return;
    }
    if (!redirectResult) {
      // Not a redirect return, so any uuid still in storage is left over from
      // an abandoned attempt. Drop it now rather than let it get attached to
      // an unrelated challenge later.
      this.clearPendingAdyenPaymentUuid();
      return;
    }
    // Strip it so a refresh does not replay a consumed result.
    url.searchParams.delete(ADYEN_REDIRECT_RESULT_PARAM);
    window.history.replaceState({}, '', url.toString());

    if (!this.getPendingAdyenPaymentUuid()) {
      // Came back with a result we cannot attach to a payment — treat as a
      // failed attempt rather than silently doing nothing.
      this.mixPanel.adyenError({ 'User Type': 'Driver', Phase: 'redirect-return-no-uuid' });
      return;
    }
    this.isLoading = true;
    try {
      const result = checkout.submitDetails({ details: { redirectResult } });
      if (result && typeof result.catch === 'function') {
        result.catch((err: unknown) => this.onAdyenError(err));
      }
    } catch (err) {
      this.onAdyenError(err);
    }
  }

  private maybeMountAdyen(): void {
    if (!this.useAdyen || !this.adyenCheckout || !this.adyenRootEl
        || this.adyenCardComponent || this.adyenMountScheduled) {
      return;
    }
    // Defer so #adyenRoot is present in the DOM before Drop-in mounts into it.
    this.adyenMountScheduled = true;
    setTimeout(() => {
      this.adyenMountScheduled = false;
      this.mountAdyenCard();
    }, 0);
  }

  private mountAdyenCard(): void {
    if (!this.useAdyen || !this.adyenCheckout || !this.adyenRootEl || this.adyenCardComponent) {
      return;
    }
    try {
      // Adyen Web v6 is fully modular: Drop-in renders nothing unless the
      // payment-method component classes are registered here. Without
      // `paymentMethodComponents` the container mounts empty with no error.
      const dropin = new Dropin(this.adyenCheckout, {
        paymentMethodComponents: [Card],
        // Signup keeps its own Back/Next footer, so suppress Drop-in's pay
        // button and drive submission from submitProfile() instead.
        showPayButton: false,
        paymentMethodsConfiguration: {
          card: {
            hasHolderName: false,
            holderNameRequired: false,
            // Signup collects postal code in its own field below and sends it
            // as billingAddress.zipcode, so Drop-in must not ask again.
            billingAddressRequired: false,
            enableStoreDetails: false,
            hideCVC: false,
            // Card-number field reports the last 4 once it validates; used for
            // the saved-card summary after authorization.
            onFieldValid: (event: any) => this.zone.run(() => {
              if (event && event.endDigits) {
                this.adyenLast4 = event.endDigits;
              }
            }),
            onBrand: (event: any) => this.zone.run(() => this.setCardIconByBrand(event?.brand)),
          },
        },
      } as any);
      dropin.mount(this.adyenRootEl);
      this.adyenCardComponent = dropin;
    } catch (err) {
      this.onAdyenError(err);
      this.zone.run(() => { this.useAdyen = false; });
    }
  }

  private setCardIconByBrand(brand: string): void {
    switch ((brand || '').toLowerCase()) {
      case 'visa':
        this.ccTypeUrl = '/assets/images/ic_credit_visa.png';
        this.ccTypeAltText = 'VISA';
        break;
      case 'mc':
      case 'mastercard':
        this.ccTypeUrl = '/assets/images/mc_symbol.svg';
        this.ccTypeAltText = 'Mastercard';
        break;
      case 'amex':
        this.ccTypeUrl = '/assets/images/ic_credit_amex.png';
        this.ccTypeAltText = 'American Express';
        break;
      case 'discover':
        this.ccTypeUrl = '/assets/images/ic_credit_discover_cp.png';
        this.ccTypeAltText = 'Discover';
        break;
      default:
        this.ccTypeUrl = '/assets/images/ic_credit_generic.png';
        this.ccTypeAltText = '';
        break;
    }
  }

  // Mirrors the validators declared on payForm, for the fallback path where
  // Adyen is torn down after clearGpCardValidators() has already run.
  private restoreGpCardValidators(): void {
    const validators: { [name: string]: any[] } = {
      creditcard: [Validators.required],
      expirationdate: [Validators.required, this.isExpirationCorrect()],
      cvv: [Validators.required],
    };
    Object.keys(validators).forEach((controlName) => {
      const control = this.payForm.get(controlName);
      if (control) {
        control.setValidators(validators[controlName]);
        control.updateValueAndValidity();
      }
    });
  }

  private clearGpCardValidators(): void {
    ['creditcard', 'expirationdate', 'cvv'].forEach((controlName) => {
      const control = this.payForm.get(controlName);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });
  }

  private submitAdyenProfile(): void {
    // Promo-only / already-saved-card cases reuse the shared flow.
    if (this.paymentPromoPreviouslySet() || this.ccLast4) {
      this.executePaymentFlow();
      return;
    }
    const postalControl = this.payForm.get('postalcode');
    const cardRequired = !this.acceptedPromo || this.responsePromo?.paymentRequired;
    const userEnteredCard = this.adyenValid || this.adyenTouched;

    // Promo fully covers payment and the user left the card blank → promo only.
    if (!cardRequired && !userEnteredCard) {
      this.executePaymentFlow();
      return;
    }

    if (this.adyenValid && postalControl.valid) {
      this.executePaymentFlow();
    } else {
      // Surface inline field errors, mirroring the GP form's behaviour.
      if (this.adyenCardComponent && typeof this.adyenCardComponent.showValidation === 'function') {
        this.adyenCardComponent.showValidation();
      }
      postalControl.markAsTouched();
    }
  }

  // Drop-in hands us the encrypted card here after dropin.submit(). This is the
  // only point at which the blobs exist — they are never held on the component.
  private onAdyenDropinSubmit(state: any, component: any): void {
    const encrypted = (state?.data?.paymentMethod ?? {}) as AdyenEncryptedCard;
    // Drop-in collects browserInfo (screen size, timezone, java/JS flags) for
    // Adyen's risk + 3DS device fingerprint. Adyen rejects the payment when it
    // is missing on a Web channel call, so forward it verbatim.
    const browserInfo = state?.data?.browserInfo as Record<string, unknown> | undefined;
    this.zone.run(() => {
      this.isLoading = true;
      this.authorizeAdyenFlow(encrypted, browserInfo).then(
        (resp) => this.handleAdyenResult(resp, component),
        (err) => this.failAdyen(component, err)
      );
    });
  }

  // Second leg of a 3DS challenge. The SDK calls this once the shopper has
  // cleared the fingerprint or challenge, with the details Adyen needs to
  // finish the payment. paymentUuid is ours, not the SDK's — it comes from the
  // authorize response and has to survive a full-page redirect, hence
  // sessionStorage.
  private onAdyenAdditionalDetails(state: any, component: any): void {
    const target = component || this.adyenCardComponent;
    const paymentUuid = this.getPendingAdyenPaymentUuid();
    if (!paymentUuid) {
      // Nothing to attach the details to; the payment cannot be completed.
      this.mixPanel.adyenError({ 'User Type': 'Driver', Phase: '3ds-details-no-uuid' });
      this.failAdyen(target, { error: { errorId: 401 } });
      return;
    }
    this.zone.run(() => {
      this.isLoading = true;
      this.userService.submitAdyen3dsDetails(
        {
          details: (state?.data?.details ?? {}) as Record<string, unknown>,
          paymentUuid,
          paymentData: state?.data?.paymentData,
        },
        this.commSvc.getSessionToken()
      ).then(
        (resp) => {
          this.mixPanel.adyenChallengeCompleted({
            'User Type': 'Driver', 'Result Code': resp?.resultCode,
          });
          this.handleAdyenResult(resp, target);
        },
        (err) => this.failAdyen(target, err)
      );
    });
  }

  // Where Adyen sends the shopper back after a redirect-based challenge.
  // Adyen also validates the field on every authorize call even when no
  // redirect follows, so this must always be a real, absolute URL. Built off
  // the live location rather than a hard-coded route so it stays correct under
  // every deployment base href (and under the webview/driver-connection
  // shells) — and so the driver lands back on the payment step itself.
  //
  // The query string is carried over because this app reads its configuration
  // straight off window.location.search on load, not from the router: `brand`
  // and `subdomain` drive theming (theme.service) and `redirect` drives where
  // signup sends the driver afterwards (communication.service). Returning to a
  // bare path would silently drop the partner theme and the redirect target.
  // Same reason the PayPal return/cancel URLs above append it.
  private getAdyenReturnUrl(): string {
    return `${window.location.origin}${window.location.pathname}${window.location.search}`;
  }

  private async authorizeAdyenFlow(
    encrypted: AdyenEncryptedCard,
    browserInfo?: Record<string, unknown>
  ): Promise<AdyenAuthorizeCardResponse> {
    const masterObj = this.commSvc.getMasterObject();
    if (!masterObj['givenName'] && this.userFromSession?.user?.givenName) {
      masterObj['givenName'] = this.userFromSession.user.givenName;
    }
    if (!masterObj['familyName'] && this.userFromSession?.user?.familyName) {
      masterObj['familyName'] = this.userFromSession.user.familyName;
    }
    this.mixPanel.adyenSubmit({ 'User Type': 'Driver' });

    const fullName = `${masterObj['givenName'] || ''} ${masterObj['familyName'] || ''}`.trim()
      || this.userFromSession?.user?.fullName || '';
    const request: AdyenAuthorizeCardRequest = {
      encryptedCardNumber: encrypted.encryptedCardNumber || '',
      encryptedExpiryMonth: encrypted.encryptedExpiryMonth || '',
      encryptedExpiryYear: encrypted.encryptedExpiryYear || '',
      encryptedSecurityCode: encrypted.encryptedSecurityCode || '',
      currency: this.currencyCode,
      // TODO(backend-confirm): paymentType for the signup context. Mirrors the
      // web-driver Adyen flow; the legacy GP signup used 'AUTO_TOP'.
      paymentType: 'INITIAL_DEPOSIT_ON_SIGNUP',
      amount: 0,
      shopperInfo: {
        userId: String(this.userFromSession?.user?.userId ?? ''),
        email: masterObj['email'] || this.userFromSession?.user?.email || '',
        fullName,
      },
      billingAddress: {
        // Signup only collects postal code + country (same as the legacy GP form).
        address1: '',
        city: '',
        state: '',
        zipcode: this.payForm.get('postalcode').value || this.userFromSession?.address?.zipCode || '',
        country: (this.countryCode as string) || '',
      },
      adyenAdditionalData: {
        returnUrl: this.getAdyenReturnUrl(),
        channel: 'Web',
        checkoutAttemptId: this.adyenCheckoutAttemptId,
        browserInfo,
      },
    };

    return this.userService.authorizeAdyenCard(request, this.commSvc.getSessionToken());
  }

  // Single funnel for both legs (authorize and 3ds-details) because Adyen can
  // answer a challenge with another challenge — fingerprint first, then the
  // real one — so the details response has to be able to loop back through here.
  private handleAdyenResult(resp: AdyenAuthorizeCardResponse, component: any): void {
    // Keep the uuid before anything else: a redirect-type action navigates the
    // page away immediately and we need it again when we come back.
    if (resp?.paymentUuid) {
      this.setPendingAdyenPaymentUuid(resp.paymentUuid);
    }

    // Presence of an action wins over resultCode, matching web-driver and both
    // mobile apps. The action object is handed to the SDK untouched.
    const action = resp?.['3dsAction'];
    if (action) {
      this.mixPanel.adyenChallenge({
        'User Type': 'Driver', 'Result Code': resp.resultCode, 'Action Type': action.type,
      });
      this.zone.run(() => {
        // Drop the spinner so the challenge UI the SDK mounts is visible and
        // interactive; the flow is no longer waiting on us.
        this.isLoading = false;
      });
      if (component && typeof component.handleAction === 'function') {
        component.handleAction(action);
      } else {
        // No component to run the challenge on — fail rather than hang.
        this.mixPanel.adyenError({ 'User Type': 'Driver', Phase: '3ds-no-component' });
        this.failAdyen(component, { error: { errorId: 401 } });
      }
      return;
    }

    switch (resp?.resultCode) {
      case 'Authorised':
        this.mixPanel.adyenAuthorized({ 'User Type': 'Driver' });
        this.clearPendingAdyenPaymentUuid();
        if (component && typeof component.setStatus === 'function') {
          component.setStatus('success');
        }
        this.zone.run(() => this.onAuthorizeSuccess({ paymentUuid: resp.paymentUuid }));
        return;
      case 'Refused':
        this.mixPanel.adyenRefused({ 'User Type': 'Driver', Reason: resp.refusalReason });
        this.failAdyen(component, { error: { errorId: resp.errorId ?? 401 } });
        return;
      case 'RedirectShopper':
      case 'IdentifyShopper':
      case 'ChallengeShopper':
        // A challenge resultCode with no action is a backend contract breach:
        // Adyen told us to authenticate but gave us nothing to run.
        this.mixPanel.adyenError({
          'User Type': 'Driver',
          'Result Code': resp.resultCode,
          Note: '3DS challenge resultCode with no action',
        });
        this.failAdyen(component, { error: { errorId: 401 } });
        return;
      default:
        this.mixPanel.adyenError({
          'User Type': 'Driver', 'Result Code': resp?.resultCode, Error: resp?.errorMessage,
        });
        this.failAdyen(component, { error: { errorId: resp?.errorId ?? 500 } });
    }
  }

  // Terminal failure for the Adyen path: clear any half-finished challenge,
  // return Drop-in to an editable state so the driver can retry, and surface
  // the error through the same handler the legacy GP path uses.
  private failAdyen(component: any, err: any): void {
    this.clearPendingAdyenPaymentUuid();
    this.zone.run(() => {
      if (component && typeof component.setStatus === 'function') {
        component.setStatus('ready');
      }
      this.onAuthorizeError(err);
    });
  }

  // The uuid has to outlive a full-page redirect (Adyen can answer with a
  // redirect action rather than an in-page challenge), so it lives in
  // sessionStorage alongside the rest of signup's flow state.
  private setPendingAdyenPaymentUuid(paymentUuid: string): void {
    try {
      window.sessionStorage.setItem(ADYEN_PENDING_PAYMENT_UUID_KEY, paymentUuid);
    } catch {
      // Private-mode / storage-disabled: the in-page challenge still works,
      // only the redirect leg is lost.
    }
  }

  private getPendingAdyenPaymentUuid(): string | undefined {
    try {
      return window.sessionStorage.getItem(ADYEN_PENDING_PAYMENT_UUID_KEY) || undefined;
    } catch {
      return undefined;
    }
  }

  private clearPendingAdyenPaymentUuid(): void {
    try {
      window.sessionStorage.removeItem(ADYEN_PENDING_PAYMENT_UUID_KEY);
    } catch {
      // nothing to do
    }
  }

  // Shared success/error handlers for both the Adyen and GP authorize paths.
  private onAuthorizeSuccess(data: any): void {
    if (data.paymentUuid) {
      this.masterObj.paymentUuid = data.paymentUuid;
      this.masterObj.ccLast4 = this.useAdyen
        ? this.adyenLast4
        : this.payForm.get('creditcard').value.slice(-4);
    }
    const formObj = data.paymentUuid ? { paymentUuid: data.paymentUuid } : { paymentUuid: '' };
    const backendSaveObj = new Payment(formObj);
    if (this.acceptedPromo) {
      data.promo = this.responsePromo;
    }
    this.isLoading = false;
    this.commSvc.submitFormFields(data, backendSaveObj);
  }

  private onAuthorizeError(err: any): void {
    if (!err.error || typeof err.error.errorId === 'undefined') {
      this.invalidCreditCardCode = 429;
    } else {
      this.invalidCreditCardCode = err.error.errorId;
    }
    this.isLoading = false;
  }

  private unmountAdyen(): void {
    try {
      if (this.adyenCardComponent && typeof this.adyenCardComponent.unmount === 'function') {
        this.adyenCardComponent.unmount();
      }
    } catch {
      // ignore unmount races
    }
    this.adyenCardComponent = null;
  }

  private onAdyenError(err: unknown): void {
    this.mixPanel.adyenError({ 'User Type': 'Driver', Phase: 'sdk' });
    // eslint-disable-next-line no-console
    console.error('Adyen error', err);

    // Adyen raises this once its secured-field iframes never confirm that they
    // configured — in practice, when the page origin is missing from the Adyen
    // client key's allowed-origins list. The card inputs are inert at that
    // point, so without this the driver is stranded on a form they cannot type
    // into and cannot escape (the GP inputs are unrendered and de-validated).
    // Read `message` off the object rather than via `instanceof Error`:
    // AdyenCheckoutError crosses a bundle boundary and may not match.
    const raw = (err as { message?: unknown } | null | undefined)?.message;
    const message = typeof raw === 'string' ? raw : String(err ?? '');
    if (/secured fields have failed to configure/i.test(message)) {
      this.zone.run(() => {
        this.unmountAdyen();
        this.useAdyen = false;
        this.adyenValid = false;
        this.adyenTouched = false;
        this.restoreGpCardValidators();
      });
    }
  }

  ngOnDestroy(): void {
    this.unmountAdyen();
  }

  getHeaderParagraph(): string {
    if (this.signupType === 'LEASECO') {
      return this.langPipe.transform('%orgName% will pay for charging for business purposes. To pay for charging that is for personal purposes, set up a payment method.', {orgName: this.orgName} );
    }
    if (this.isPayPerCharge) {
      return this.langPipe.transform('PayAsYouGoHeadline');
    }

    return this.langPipe.transform('We’ll load your ChargePoint account with %1% the first time you use a station with a fee (set by the owner).', this.formattedCost);
  }
}
